// @flow
import * as React from 'react';
import { Box, ButtonBase, Collapse, Divider, Grid, Stack, Typography } from '@mui/material';
import { darkenHexColor, letterToColorfulHex, lightenHexColor, UserAvatar } from '../../../../../../shared';
import { CaretDown, CaretRight, Info } from '@phosphor-icons/react';
import { Tooltip } from '@coverright/ui/inputs';
import { useState } from 'react';
import { CallTimeline, CallTimelineParam } from './lib';

type Props = {
  timeline: CallTimeline,
  onClick: (amount: number) => void
};
export const Timeline = ({timeline, onClick}: Props) => {
  const [open, setOpen] = useState(false);
  return (
    <Stack>
      <Stack gap={2} direction={'row'} alignItems={'center'} mb={1.5}>
        <UserAvatar {...timeline.participant} size={24} fontSize={11} />
        <Divider orientation={'vertical'} sx={{alignSelf: 'stretch'}} />
        <ButtonBase onClick={() => setOpen(p => !p)}>
          <Typography variant={'caption'} sx={{mr: 1, textDecoration: 'underline'}}>{open ? 'Hide' : 'Show'} rates</Typography>
          <CaretDown weight={'bold'} style={{transition: 'all .3s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)'}} />
        </ButtonBase>
      </Stack>

      <Line color={letterToColorfulHex(timeline.participant.email)} onClick={onClick} timeline={timeline} />

      <Collapse in={open}>
        <Grid container spacing={2} sx={{mt: 0}}>
          <CallParam title={'Question Ratio'}
                     tooltip={'Ratio of questions asked by the user during the meeting.'}
                     value={`${timeline.questionRatio.value}%`}
                     status={timeline.questionRatio.status}
                     suggested={'10% - 30%'} />
          <CallParam title={'Talk Ratio'}
                     tooltip={'Percentage of time the user spoke during the meeting.'}
                     value={`${timeline.talkRatio.value}%`}
                     status={timeline.talkRatio.status}
                     suggested={`${timeline.talkRatio.lowestSuggestedValue}% - ${timeline.talkRatio.highestSuggestedValue}%`} />
          <CallParam title={'Talking Speed'}
                     tooltip={'Average speech tempo during the meeting.'}
                     value={`${timeline.talkingSpeed.value} words/min`}
                     status={timeline.talkingSpeed.status}
                     suggested={'170 - 190'} />
          <CallParam title={'Average Patience'}
                     tooltip={'Average duration that a person waits to begin speaking.'}
                     value={`${timeline.averagePatience.value} Seconds`}
                     status={timeline.averagePatience.status}
                     suggested={'0.7 - 1.5'} />
          <CallParam title={'Language Positivity'}
                     tooltip={'Average positivity rate of used language during the meeting.'}
                     value={`${timeline.languagePositivity.value}% Positive`}
                     status={timeline.languagePositivity.status}
                     suggested={'30% - 100%'} />
          <CallParam title={'Voice Emotion'}
                     tooltip={'Emotion of the voice used by the speaker.'}
                     value={`${timeline.voiceEmotion.value}% Happy`}
                     status={timeline.voiceEmotion.status}
                     suggested={'50% - 100%'} />
        </Grid>
      </Collapse>
    </Stack>
  );
};

type CallParamProps = {
  title: React.ReactNode,
  value: React.ReactNode,
  suggested: React.ReactNode,
  status: CallTimelineParam['status'],
  tooltip: React.ReactNode,
}

const CallParam = (props: CallParamProps) => (
  <Grid item xs={6}>
    <Stack border={'1px solid #d6d6d6'} gap={.3} p={'6px 8px'} borderRadius={'4px'}>
      <Stack gap={.5} direction={'row'} alignItems={'flex-start'}>
        <Typography variant={'caption'}>{props.title}</Typography>
        <Tooltip title={props.tooltip}>
          <Info size={10} color="#737373" />
        </Tooltip>
      </Stack>
      <Typography color={`${props.status === 'good' ? 'success' : props.status === 'bad' ? 'error' : 'warning'}.500`} fontWeight={500}>{props.value}</Typography>
      <Typography variant={'caption'} fontSize={11} color={'rgb(115,115,115)'}>
        Suggested: <Typography  variant={'caption'} fontWeight={500} color={'text.primary'}>{props.suggested}</Typography>
      </Typography>
    </Stack>
  </Grid>
)

const Line = ({color, timeline, onClick}: {color: string, timeline: CallTimeline, onClick: (amount: number) => void}) => {
  const getPercent = (value: number) => value * 100 / timeline.callTime;

  return <Box position={'relative'} bgcolor={lightenHexColor(color, 80)} height={6} width={1} borderRadius={'2px'}>
    {timeline.timeline.map((item, i: number) => (
      <Tooltip title={`${formatTime(item.start_time)} - ${formatTime(item.end_time)}`} key={i}>
        <Box position={'absolute'}
             onClick={() => onClick(item.start_time)}
             left={getPercent(item.start_time) + '%'}
             width={getPercent(item.end_time - item.start_time) + '%'} top={0} height={6} bgcolor={darkenHexColor(color, 10)}
             sx={{'&:hover': {height: 8, top: -1}, cursor: 'pointer' }}
             borderRadius={'2px'} />
      </Tooltip>
    ))}
  </Box>
}

function formatTime(seconds: number) {
  // Handle non-numbers, negatives, NaN, Infinity
  if (seconds < 0 || !isFinite(seconds)) {
    return '00:00';
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  // Pad with leading zeros
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
